<template>
	<div class="c-podcast-player__container">
		<slot name="introduction"></slot>
		<audio v-show="!isBrowserSupportsAudio"
			   class="c-podcast-player__video"
			   preload="metadata"
			   controls
			   playsinline
			   @loadedmetadata="initializePlayer($event)"
			   @timeupdate="timeUpdate()"
			   data-testid="audioPlayer">
			<source :src="podcastUrl" type="audio/mpeg">
		</audio>
		<div class="c-podcast-player__controls"
			 v-if="isBrowserSupportsAudio">
			<div class="c-podcast-player__time">
				<input ref="podcastTimeBar"
					   class="c-podcast-player__progressbar --time"
					   :value="progressCurrentTime"
					   min="0"
					   :max="duration"
					   type="range"
					   step="0.1"
					   @input="updateTime($event.target.value)"
					   data-testid="timeProgressBar" />
				<time :datetime="currentTime" class="c-podcast-player__currenttime">{{ currentTime }}</time>
				<time class="c-podcast-player__duration">{{ podcastDuration }}</time>
			</div>
			<button type="button"
					@click="playPodcast()"
					class="c-podcast-player__buttons"
					data-testid="playPauseButton">
				<svg>
					<use xlink:href="#play-icon" v-if="!podcastIsPlaying"></use>
					<use xlink:href="#pause-button" v-else></use>
				</svg>
			</button>
			<div class="c-podcast-player__volume"
				 @mouseover="volumeExpanded=true"
				 @mouseleave="volumeExpanded=false"
				 :class="{ '--volumebarshown' : volumeExpanded && !isIOS }">
				<input class="c-podcast-player__progressbar --volume"
					   :class="{ '--hidden' : isIOS }"
					   ref="volumeProgressBar"
					   value="1"
					   type="range"
					   max="1"
					   min="0"
					   step="0.01"
					   @input="setVolume($event.target)"
					   data-testid="volumeProgressBar" />
				<button class="c-podcast-player__volume-button"
						:class="{ '--muted' : muted  }"
						@click="toggleMute"
						data-testid="volumeMute">
					<svg class="c-podcast-player__volume-icon">
						<use xlink:href="#speaker-icon"></use>
					</svg>
				</button>
			</div>
			<button type="button"
					@click="setIfSpeedBarExpanded()"
					class="c-podcast-player__settings"
					data-testid="volumeSpeedShowHide">
				<svg><use xlink:href="#player-speed"></use></svg>
			</button>
			<div v-show="speedExpanded"
				 class="c-podcast-player__speed"
				 data-testid="playerspeed">
				<button class="c-podcast-player__speedlabel"
						type="button"
						@click="setIfSpeedBarExpanded()">
					{{ vm.BaseViewModel.PodcastPlayerSettings.SelectPlaySpeedLabel }}
				</button>
				<label v-for="(speed, label) in podcastSpeed"
					   :key="`label-${blockID}`">
					<input class="c-podcast-player__speedcheckbox"
						   type="radio"
						   :name="`podcastSpeed--${blockID}`"
						   :checked="speedValue == speed"
						   @change="changeSpeed(speed)"
						   data-testid="playerSpeedValues" />
					<span>{{ vm.BaseViewModel.PodcastPlayerSettings.PlaySpeedLabels[label] }}</span>
				</label>
			</div>
		</div>
	</div>
</template>
<script>
	import { track_content_engagement } from 'shared/services/tracking/contentTrackingService';

	export default {
		name: "podcast-player-block",
		props: ['vm'],
		data: function () {
			return {
				podcastUrl: this.vm.CurrentBlock.PodcastUrl,
				podcast: null,
				currentTime: '00:00',
				progressCurrentTime: 0,
				podcastIsPlaying: false,
				podcastDuration: 0,
				duration: 0,
				speedValue: 1,
				podcastSpeed: {
					'Slow': 0.75,
					'Normal' : 1,
					'Fast': 1.5
				},
				speedExpanded: false,
				volumeExpanded: false,
				podcastVolume: 1,
				muted: false,
				blockID: this.vm.BaseViewModel.CurrentBlockId,
			}
		},
		computed: {
			isBrowserSupportsAudio() {
				return !!document.createElement('audio').canPlayType;
			},
			isIOS() {
				let platform = navigator?.userAgent || navigator?.platform || 'unknown';
				return /iPhone|iPod|iPad/.test(platform);
			},
		},
		created() {
			window.addEventListener('mouseover', (e) => {
				if (!this.$el.contains(e.target)) {
					this.speedExpanded = false;
				}
			})
		},
		watch: {
			muted(newValue) {
				this.podcast.muted = newValue;
			}
		},
		methods: {
			formatTime(timeInSeconds) {
				const result = new Date(timeInSeconds * 1000).toISOString().slice(11, 19);
				return `${result.slice(3, 5)}:${result.slice(6, 8)}`;
			},
			initializePlayer(event) {
				this.podcast = event.target;
				this.duration = this.podcast.duration;
				this.podcastDuration = this.formatTime(this.duration);
			},
			timeUpdate() {
				this.progressCurrentTime = this.podcast.currentTime;
				this.currentTime = this.formatTime(this.podcast.currentTime);
				this.handleProgress(this.$refs.podcastTimeBar);
				if (this.podcast.ended) {
					this.podcastIsPlaying = false;
				}
			},
			updateTime(podcastTime) {
				this.podcast.currentTime = podcastTime;
			},
			handleProgress(progressBar) {
				progressBar.style.backgroundSize = (progressBar.value - progressBar.min) * 100 / (progressBar.max - progressBar.min) + '% 100%';
			},
			playPodcast() {
				this.podcastIsPlaying = !this.podcastIsPlaying;
				if (this.podcast.paused) {
					this.podcast.play();
					track_content_engagement('podcast', 'start', this.vm.CurrentBlock.Headline || 'no_data', this.vm.CurrentBlock.PodcastUrl);
				}
				else {
					this.podcast.pause();
				}
			},
			changeSpeed(speed) {
				this.speedValue = speed;
				this.podcast.playbackRate = speed;
				this.speedExpanded = !this.speedExpanded;
			},
			setVolume(target) {
				if (this.muted) {
					this.muted = false;
				}
				if (target.value == 0) {
					this.muted = true;
				}
				this.podcast.volume = target.value;
				this.handleProgress(target);
			},
			toggleMute() {
				const volumeProgressBar = this.$refs.volumeProgressBar;
				this.muted = !this.muted;
				if (this.muted) {
					this.podcastVolume = this.podcast.volume;
					this.podcast.volume = 0;
					volumeProgressBar.value = 0;
				}
				else {
					this.podcast.volume = this.podcastVolume;
					volumeProgressBar.value = this.podcastVolume;
				}
				this.handleProgress(volumeProgressBar);
			},
			setIfSpeedBarExpanded() {
				this.speedExpanded = !this.speedExpanded;
			},
		},
	};
</script>
